import { produce } from "immer";
import { runtimeEvents } from "@shared/runtime-events";
import { blastApi } from "@features/flows/api/blast-api";
import { useRunControlStore, type RunControlState } from "../store";
import { MAX_ERRORS_PER_NODE, MAX_OUTPUTS_PER_NODE } from "../constants";
import type { Snapshot, RunStatus } from "../types";
import {
  createRunEventContext,
  isTerminalStatus,
  reduceRunEvent,
  reduceSnapshot,
  type RunEventReduction,
  type RunEventMeta,
} from "./reducers";

type DraftMutator = (draft: RunControlState) => void;

const ctx = createRunEventContext();

let telemetryEs: EventSource | null = null;
let snapshotEs: EventSource | null = null;

let queue: DraftMutator[] = [];
let scheduled = false;
let telemetryStatusGuard: ((status: RunStatus) => boolean) | null = null;

const requestFrame: (cb: FrameRequestCallback) => number =
  typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
    ? window.requestAnimationFrame.bind(window)
    : (cb: FrameRequestCallback) => setTimeout(() => cb(Date.now()), 16) as unknown as number;

const flushQueue = () => {
  const batch = queue;
  if (!batch.length) {
    scheduled = false;
    return;
  }
  queue = [];
  scheduled = false;

  useRunControlStore.setState((current) =>
    produce(current, (draft) => {
      for (const mut of batch) {
        mut(draft);
      }
    })
  );
};

const rafBatch = (mutator: DraftMutator) => {
  queue.push(mutator);
  if (scheduled) return;
  scheduled = true;
  requestFrame(flushQueue);
};

const applyRunReduction = (reduction: RunEventReduction | null) => {
  if (!reduction) return;
  rafBatch((draft) => {
    if (reduction.status) {
      if (telemetryStatusGuard && !telemetryStatusGuard(reduction.status)) {
        // Skip applying telemetry status if guard rejects it
      } else {
        draft.status = reduction.status;
        if (reduction.status === "running") {
          draft.pauseReason = null;
          draft.currentSuspendedNodeId = null;
        }
        if (isTerminalStatus(reduction.status)) {
          draft.pauseReason = null;
          draft.currentSuspendedNodeId = null;
        }
      }
    }

    if (reduction.frames) {
      draft.runningNodeIds = reduction.frames.running;
      draft.activeNodeIds = reduction.frames.running;
      draft.waitingNodeIds = reduction.frames.waiting;
      draft.suspendedNodeIds = reduction.frames.suspended;
    }

    if (reduction.metrics) {
      if (reduction.metrics.queueLen !== undefined) draft.queueLen = reduction.metrics.queueLen;
      if (reduction.metrics.stackLen !== undefined) draft.stackLen = reduction.metrics.stackLen;
      if (reduction.metrics.suspendedLen !== undefined) draft.suspendedLen = reduction.metrics.suspendedLen;
    }

    if (reduction.completedNodeIds) {
      reduction.completedNodeIds.forEach((nodeId) => {
        draft.completedNodeIds.add(nodeId);
      });
    }

    if (reduction.failedNodeIds) {
      reduction.failedNodeIds.forEach((nodeId) => {
        draft.failedNodeIds.add(nodeId);
      });
    }

    if (reduction.attempts) {
      reduction.attempts.forEach(({ nodeId, delta }) => {
        draft.nodeAttemptCounts.set(nodeId, (draft.nodeAttemptCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.successes) {
      reduction.successes.forEach(({ nodeId, delta }) => {
        draft.nodeSuccessCounts.set(nodeId, (draft.nodeSuccessCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.failures) {
      reduction.failures.forEach(({ nodeId, delta }) => {
        draft.nodeFailureCounts.set(nodeId, (draft.nodeFailureCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.nodeError) {
      const entry = reduction.nodeError;
      const list = draft.nodeErrors.get(entry.nodeId) ?? [];
      if (!list.some((existing) => existing.id === entry.id)) {
        list.push(entry);
        if (list.length > MAX_ERRORS_PER_NODE) {
          list.splice(0, list.length - MAX_ERRORS_PER_NODE);
        }
        draft.nodeErrors.set(entry.nodeId, list);
        draft.lastNodeError = entry;
      }
    }

    if (reduction.nodeOutput) {
      const entry = reduction.nodeOutput;
      const list = draft.nodeOutputs.get(entry.nodeId) ?? [];
      if (!list.some((existing) => existing.id === entry.id)) {
        list.push(entry);
        if (list.length > MAX_OUTPUTS_PER_NODE) {
          list.splice(0, list.length - MAX_OUTPUTS_PER_NODE);
        }
        draft.nodeOutputs.set(entry.nodeId, list);
        draft.lastNodeOutput = entry;
      }
    }
  });
};

const applySnapshotReduction = (
  reduction: ReturnType<typeof reduceSnapshot>,
  onPaused: (paused: boolean | null, headNodeId: string | null, snapshot: Snapshot) => void
) => {
  rafBatch((draft) => {
    draft.snapshot = reduction.snapshot;
    draft.queueLen = reduction.metrics.queueLen;
    draft.stackLen = reduction.metrics.stackLen;
    draft.suspendedLen = reduction.metrics.suspendedLen;
    draft.runningNodeIds = reduction.frames.running;
    draft.activeNodeIds = reduction.frames.running;
    draft.waitingNodeIds = reduction.frames.waiting;
    draft.suspendedNodeIds = reduction.frames.suspended;

    if (reduction.completedNodeIds) {
      reduction.completedNodeIds.forEach((nodeId) => {
        draft.completedNodeIds.add(nodeId);
      });
    }

    if (reduction.failedNodeIds) {
      reduction.failedNodeIds.forEach((nodeId) => {
        draft.failedNodeIds.add(nodeId);
      });
    }

    if (reduction.attempts) {
      reduction.attempts.forEach(({ nodeId, delta }) => {
        draft.nodeAttemptCounts.set(nodeId, (draft.nodeAttemptCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.successes) {
      reduction.successes.forEach(({ nodeId, delta }) => {
        draft.nodeSuccessCounts.set(nodeId, (draft.nodeSuccessCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.failures) {
      reduction.failures.forEach(({ nodeId, delta }) => {
        draft.nodeFailureCounts.set(nodeId, (draft.nodeFailureCounts.get(nodeId) ?? 0) + delta);
      });
    }

    if (reduction.errors) {
      reduction.errors.forEach((entry) => {
        const list = draft.nodeErrors.get(entry.nodeId) ?? [];
        if (!list.some((existing) => existing.id === entry.id)) {
          list.push(entry);
          if (list.length > MAX_ERRORS_PER_NODE) {
            list.splice(0, list.length - MAX_ERRORS_PER_NODE);
          }
          draft.nodeErrors.set(entry.nodeId, list);
          draft.lastNodeError = entry;
        }
      });
    }

    if (reduction.outputs) {
      reduction.outputs.forEach((entry) => {
        const list = draft.nodeOutputs.get(entry.nodeId) ?? [];
        if (!list.some((existing) => existing.id === entry.id)) {
          list.push(entry);
          if (list.length > MAX_OUTPUTS_PER_NODE) {
            list.splice(0, list.length - MAX_OUTPUTS_PER_NODE);
          }
          draft.nodeOutputs.set(entry.nodeId, list);
          draft.lastNodeOutput = entry;
        }
      });
    }
  });
  onPaused(reduction.paused, reduction.suspendedHeadNodeId ?? null, reduction.snapshot);
};

const getMeta = (): RunEventMeta => {
  const state = useRunControlStore.getState();
  return {
    runId: state.runId ?? "",
    flowId: state.flowId,
  };
};

export const disconnectTelemetrySse = () => {
  if (telemetryEs) {
    try {
      telemetryEs.close();
    } catch {
      // noop
    }
    telemetryEs = null;
    useRunControlStore.getState().setTelemetryConnected(false);
  }
};

export const disconnectSnapshotSse = () => {
  if (snapshotEs) {
    try {
      snapshotEs.close();
    } catch {
      // noop
    }
    snapshotEs = null;
    useRunControlStore.getState().setSnapshotConnected(false);
  }
};

export const resetServiceRefs = () => {
  ctx.byFrame.clear();
  ctx.countedFrames.clear();
  ctx.completedFrames.clear();
  ctx.failedFrames.clear();
  ctx.runningCounts.clear();
  ctx.waitingCounts.clear();
  ctx.suspendedCounts.clear();
};

export const setTelemetryStatusGuard = (guard: ((status: RunStatus) => boolean) | null) => {
  telemetryStatusGuard = guard;
};

export const connectTelemetrySse = (runId: string, endRun: (final: RunStatus) => void) => {
  disconnectTelemetrySse();

  const es = blastApi.createEventSource(runId, { replay: "missed" });

  es.onopen = () => {
    useRunControlStore.getState().setTelemetryConnected(true);
  };

  es.addEventListener("telemetry", (event: MessageEvent) => {
    try {
      const payload = JSON.parse(event.data);
      const reduction = reduceRunEvent(payload, ctx, getMeta());
      applyRunReduction(reduction);
      try {
        runtimeEvents.emit("telemetry", payload);
      } catch {
        // ignore runtime event errors
      }
    } catch {
      // swallow malformed payloads
    }
  });

  es.addEventListener("end", (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const raw = String(data?.status ?? data?.state ?? data?.result ?? "").toLowerCase();
      let final: RunStatus | undefined;
      if (["completed", "complete", "success", "ok"].includes(raw)) final = "completed";
      else if (["failed", "fail", "error", "exception", "crashed"].includes(raw)) final = "failed";
      else if (["canceled", "cancelled", "aborted", "stopped"].includes(raw)) final = "canceled";
      if (!final && (data?.error || data?.err)) final = "failed";
      if (final) {
        try {
          runtimeEvents.emit("end", { status: final });
        } catch {
          // ignore
        }
        endRun(final);
      }
    } catch {
      // ignore parse errors
    }
  });

  es.onerror = () => {
    useRunControlStore.getState().setTelemetryConnected(false);
    if (!useRunControlStore.getState().runId) {
      try {
        es.close();
      } catch {
        // noop
      }
    }
  };

  telemetryEs = es;
};

export const connectSnapshotSse = (
  runId: string,
  setPausedFromSnapshot: (paused: boolean | null, headNodeId: string | null, snapshot: Snapshot) => void,
  endRun: (final: RunStatus) => void
) => {
  disconnectSnapshotSse();

  const es = blastApi.createSnapshotEventSource(runId, { replay: "missed", interval: 750 });

  es.onopen = () => {
    useRunControlStore.getState().setSnapshotConnected(true);
  };

  const consumeSnapshot = (value: unknown) => {
    if (!value || typeof value !== "object") return;
    const meta = getMeta();
    const reduction = reduceSnapshot(value as Snapshot, ctx, meta);
    applySnapshotReduction(reduction, setPausedFromSnapshot);
    try {
      runtimeEvents.emit("snapshot", reduction.snapshot);
    } catch {
      // ignore runtime events
    }
  };

  es.addEventListener("telemetry", (event: MessageEvent) => {
    try {
      const payload = JSON.parse(event.data);
      const snap =
        payload?.snapshot ??
        payload?.payload?.snapshot ??
        payload?.data?.snapshot ??
        payload?.state?.snapshot ??
        payload;
      consumeSnapshot(snap);
    } catch {
      // ignore parse failures
    }
  });

  es.addEventListener("snapshot", (event: MessageEvent) => {
    try {
      const payload = JSON.parse(event.data);
      consumeSnapshot(payload?.snapshot ?? payload);
    } catch {
      // ignore parse failures
    }
  });

  es.addEventListener("end", (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const raw = String(data?.status ?? data?.state ?? data?.result ?? "").toLowerCase();
      let final: RunStatus | undefined;
      if (["completed", "complete", "success", "ok"].includes(raw)) final = "completed";
      else if (["failed", "fail", "error", "exception", "crashed"].includes(raw)) final = "failed";
      else if (["canceled", "cancelled", "aborted", "stopped"].includes(raw)) final = "canceled";
      if (!final && (data?.error || data?.err)) final = "failed";
      if (final) {
        try {
          runtimeEvents.emit("end", { status: final });
        } catch {
          // ignore
        }
        endRun(final);
      }
    } catch {
      // ignore parse failures
    }
  });

  es.onerror = () => {
    useRunControlStore.getState().setSnapshotConnected(false);
    const state = useRunControlStore.getState();
    if (!state.runId || isTerminalStatus(state.status)) {
      try {
        es.close();
      } catch {
        // noop
      }
    }
  };

  snapshotEs = es;
};
